import { useEffect, useState } from 'react';
import { t } from '../../i18n';
import { fetchLeaderboard, loadIdentity, type Leaderboard } from '../../app/online';

/** A player's name, or that it is hidden (one not allowed: functions/api/leaderboard.ts). */
const shownName = (name: string) => name || <i className="dim">{t('leaderboard.hiddenName')}</i>;

/** The best-rated online players, and where this browser's player stands. */
export function LeaderboardScreen({ onBack }: { onBack: () => void }) {
  const [board, setBoard] = useState<Leaderboard | null | 'loading'>('loading');
  useEffect(() => {
    let live = true;
    void fetchLeaderboard(loadIdentity().player).then((b) => live && setBoard(b));
    return () => {
      live = false;
    };
  }, []);
  const me = board !== 'loading' ? board?.me : null;
  return (
    <main className="page page-leaderboard">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <h1>{t('leaderboard.title')}</h1>
      </header>
      <p className="lead">{t('leaderboard.intro')}</p>
      {board === 'loading' ? (
        <p className="dim" role="status">
          {t('leaderboard.loading')}
        </p>
      ) : !board ? (
        <p className="notice notice-error" role="alert">
          {t('leaderboard.unavailable')}
        </p>
      ) : board.players.length === 0 ? (
        <p className="panel">{t('leaderboard.empty')}</p>
      ) : (
        <table className="score-table leaderboard">
          <thead>
            <tr>
              <th scope="col">{t('leaderboard.rank')}</th>
              <th scope="col">{t('leaderboard.player')}</th>
              <th scope="col">{t('leaderboard.rating')}</th>
              <th scope="col">{t('leaderboard.games')}</th>
              <th scope="col">{t('leaderboard.record')}</th>
            </tr>
          </thead>
          <tbody>
            {board.players.map((p, i) => {
              const mine = !!me && me.rank === p.rank && me.name === p.name;
              return (
                <tr key={i} className={mine ? 'is-me' : ''}>
                  <td>{p.rank}</td>
                  <th scope="row">
                    {shownName(p.name)}
                    {mine && <span className="chip chip-you">{t('leaderboard.you')}</span>}
                  </th>
                  <td className="won">{p.rating}</td>
                  <td>{p.games}</td>
                  <td>
                    {p.wins}–{p.losses}–{p.draws}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {me && !board.players.some((p) => p.rank === me.rank && p.name === me.name) && (
            <tfoot>
              <tr className="is-me">
                <td>{me.rank}</td>
                <th scope="row">
                  {shownName(me.name)} <span className="chip chip-you">{t('leaderboard.you')}</span>
                </th>
                <td className="won">{me.rating}</td>
                <td>{me.games}</td>
                <td>
                  {me.wins}–{me.losses}–{me.draws}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </main>
  );
}
